import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  getDocs,
} from "firebase/firestore";
import Spinner from "./Spinner";
import { useNavContext } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import Nav from "./Nav";
import Paginador from "./Paginador";
import ProductDetailsModal from "./ProductDetailsModal";
import EditProductModal from "./EditProductModal";
import AddProductAtGroup from "./AddProductAtGroup";
import { getSubCollection } from "../utils/getSubCollection";

const GrupoProductos = () => {
  const { grupoNombre } = useParams();
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const [productos, setProductos] = useState([]);
  const [allProductsModal, setAllProductsModal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProductos, setSelectedProductos] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddProductsModalOpen, setIsAddProductsModalOpen] = useState(false);

  // 1) Suscripción real-time a `usuarios/{ownerUid}/productosClientes`
  useEffect(() => {
    setLoading(true);
    const productosCol = getSubCollection(ownerUid, "productosClientes");
    const grupoLimpio = grupoNombre.trim();
    const q = query(productosCol, where("grupo", "==", grupoLimpio));

    const unsubscribe = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProductos(
        docs.map((p) => ({
          ...p,
          nombre: p.nombreCliente || p.nombre || "Sin nombre",
          numeroDeParteCliente: p.numeroDeParteCliente || "N/A",
          costo: p.costoCliente ?? p.costo ?? "N/A",
        }))
      );
      setLoading(false);
    });

    return () => unsubscribe();
  }, [grupoNombre, ownerUid]);

  // 2) Traer TODOS los productos de `usuarios/{ownerUid}/productosClientes` para el modal
  useEffect(() => {
    const fetchAll = async () => {
      const productosCol = getSubCollection(ownerUid, "productosClientes");
      const snap = await getDocs(productosCol);
      setAllProductsModal(
        snap.docs.map((d) => {
          const p = { id: d.id, ...d.data() };
          return {
            ...p,
            nombre: p.nombreCliente || p.nombre || "Sin nombre",
            numeroDeParteCliente: p.numeroDeParteCliente || "N/A",
            costo: p.costoCliente ?? p.costo ?? "N/A",
          };
        })
      );
    };
    fetchAll();
  }, [ownerUid]);

  // 3) Filtrar localmente por búsqueda
  const filteredProductos = React.useMemo(() => {
    if (!productos.length) return [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return productos;
    return productos.filter((p) =>
      [p.nombre, p.descripcion]
        .map((f) => f?.toLowerCase() || "")
        .some((f) => f.includes(q))
    );
  }, [productos, searchTerm]);

  // 4) Paginación
  const start = (currentPage - 1) * itemsPerPage;
  const currentProducts = filteredProductos.slice(start, start + itemsPerPage);

  // 5) Selección con checkboxes
  const handleSelectAll = (e) =>
    setSelectedProductos(
      e.target.checked ? currentProducts.map((p) => p.id) : []
    );
  const handleSelectProducto = (id) =>
    setSelectedProductos((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // 6) Cambiar página / items por página
  const handleItemsPerPageChange = (n) => {
    setItemsPerPage(n);
    setCurrentPage(1);
  };
  const handlePageChange = (p) => setCurrentPage(p);

  // 7) Quitar grupo
  const handleDeleteSelected = async () => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Se quitará el grupo de los productos seleccionados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, quitar",
    });
    if (!isConfirmed) return;

    try {
      const productosCol = getSubCollection(ownerUid, "productosClientes");
      for (let id of selectedProductos) {
        await updateDoc(doc(productosCol, id), { grupo: null });
      }
      setProductos((prev) =>
        prev.filter((p) => !selectedProductos.includes(p.id))
      );
      setSelectedProductos([]);
      Swal.fire("Quitado!", "Grupo removido de los productos.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo quitar el grupo.", "error");
    }
  };

  // 8) Agregar productos al grupo
  const handleAddProductsToGroup = async (ids) => {
    try {
      const productosCol = getSubCollection(ownerUid, "productosClientes");
      for (let id of ids) {
        await updateDoc(doc(productosCol, id), {
          grupo: grupoNombre.trim(),
        });
      }
      Swal.fire("Agregado!", "Productos añadidos al grupo.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo agregar al grupo.", "error");
    }
  };

  if (loading) return <Spinner />;

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
        handleSearch={setSearchTerm}
      />

      <div className="task-filters">
        <div className="header-actions-proyectos">
          <button className="action-button-proyectos">
            <i className="bi bi-funnel-fill" /> Todos
            <i className="bi bi-chevron-down" style={{ fontSize: 12 }} />
          </button>
          <div className="buscador-pedidos">
            <i className="bi bi-search" />
            <input
              type="text"
              className="buscador-input"
              placeholder="Buscar productos relacionados..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="task-filters-right">
          <div
            className="view-buttons-container"
            style={{ position: "relative" }}
          >
            {selectedProductos.length > 0 && (
              <div className="delete-button-container">
                <button
                  className="delete-button"
                  onClick={handleDeleteSelected}
                >
                  Eliminar
                </button>
              </div>
            )}
            <button
              className="new-task-button"
              onClick={() => setIsAddProductsModalOpen(true)}
            >
              Agregar Productos
            </button>
            <button className="view-button">
              <i className="bi bi-three-dots-vertical" />
            </button>
            <button className="exportar-button">
              <i
                className="bi bi-cloud-arrow-down"
                style={{ marginRight: 5 }}
              />
              Exportar
            </button>
          </div>
        </div>
      </div>

      <div className="cotizaciones-table">
        <div className="tabla-cotizaciones-wrapper">
          <div className="tabla-cotizaciones-content">
            {productos.length === 0 ? (
              // 1️⃣ Aún no hay ningún producto relacionado
              <div className="no-resultados">
                <div
                  className="mensaje-sin-resultados"
                  style={{ marginTop: "30px" }}
                >
                  <p style={{ fontSize: "14px" }}>
                    No hay productos relacionados aún.
                  </p>
                </div>
                <div className="imagen-sin-resultados">
                  <img
                    src="/img/no-data.svg"
                    alt="Sin productos"
                    width={350}
                    height={350}
                  />
                </div>
              </div>
            ) : filteredProductos.length > 0 ? (
              // 2️⃣ Hay productos y algunos coinciden con la búsqueda
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={
                          selectedProductos.length === currentProducts.length
                        }
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Nombre</th>
                    <th>Número de Parte</th>
                    <th>Costo</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentProducts.map((p) => (
                    <tr key={p.id}>
                      <td className="check">
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={selectedProductos.includes(p.id)}
                          onChange={() => handleSelectProducto(p.id)}
                        />
                      </td>
                      <td style={{ width: "40%" }}>{p.nombre}</td>
                      <td>{p.numeroDeParteCliente}</td>
                      <td>
                        {typeof p.costo === "number"
                          ? p.costo.toFixed(2)
                          : !isNaN(Number(p.costo))
                          ? Number(p.costo).toFixed(2)
                          : "N/A"}
                      </td>
                      <td className="acciones-container">
                        <div className="acciones-icons">
                          <div
                            className="icon-wrapper"
                            onClick={() => {
                              setProductoSeleccionado(p);
                              setIsDetailsModalOpen(true);
                            }}
                          >
                            <i className="bi bi-eye" />
                            <span className="tooltipCategorias">Mostrar</span>
                          </div>
                          <div
                            className="icon-wrapper"
                            onClick={() => {
                              setProductoSeleccionado(p);
                              setIsEditModalOpen(true);
                            }}
                          >
                            <i className="bi bi-pencil" />
                            <span className="tooltipCategorias">Editar</span>
                          </div>
                          <div className="icon-wrapper">
                            <i className="bi bi-shield-slash" />
                            <span className="tooltipCategorias">
                              Desactivar
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // 3️⃣ Hay productos, pero ninguno coincide con el término de búsqueda
              <div className="no-resultados">
                <div
                  className="mensaje-sin-resultados"
                  style={{ marginTop: "30px" }}
                >
                  <p style={{ fontSize: "14px" }}>
                    No hay productos que coincidan con tu búsqueda.
                  </p>
                </div>
                <div className="imagen-sin-resultados">
                  <img
                    src="/img/noSearch.svg"
                    alt="Sin resultados"
                    className="sin-resultados-imagen"
                  />
                </div>
              </div>
            )}
          </div>

          {filteredProductos.length > 0 && (
            <div className="tabla-cotizaciones-footer">
              <div className="paginador-container">
                <Paginador
                  totalItems={filteredProductos.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <ProductDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        categoria={productoSeleccionado}
      />
      <EditProductModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        product={productoSeleccionado}
      />
      <AddProductAtGroup
        isOpen={isAddProductsModalOpen}
        onClose={() => setIsAddProductsModalOpen(false)}
        onAddToGroup={handleAddProductsToGroup}
        preloadedProducts={allProductsModal}
      />
    </>
  );
};

export default GrupoProductos;
